import { RaceCatalogueResponse } from '@/types/market';
import apiSlice from './apiSlice';

/* ---------- Interfaces ---------- */

export interface Competition {
  id?: number;
  name?: string;
  externalId?: string;
  startDate?: string | null;
}

export interface Runner {
  handicap?: number;
  runnerName?: string;
  selectionId?: string;
  sortPriority?: number;
  status?: string;
  back?: PriceSize[];
  lay?: PriceSize[];
  backPrice1?: number;
  backPrice2?: number;
  backPrice3?: number;
  layPrice1?: number;
  layPrice2?: number;
  layPrice3?: number;

  backSize1?: number;
  backSize2?: number;
  backSize3?: number;
  laySize1?: number;
  laySize2?: number;
  laySize3?: number;
}

export interface PriceSize {
  price?: number;
  size?: number;
}

/* ---------- Event List Interfaces ---------- */

export interface EventItem {
  id?: number;
  competitionId?: number;
  providerId?: number | null;
  externalId?: string;
  name?: string;
  sport?: string;
  startTime?: string;
  status?: string;
  isFancy?: boolean;
  isBookmaker?: boolean;
  isPopular?: boolean;
  inplay?: boolean;
  createdAt?: string;
  updatedAt?: string;
  competition?: Competition;
  runner?: Runner[];
}

export interface EventsQueryParams {
  sportName?: string;
  competitionId?: number;
  inplay?: boolean;
  search?: string;
  matchTime?: string;
  status?: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  fixture: EventItem[];
}
export interface RaceFixtureResponse {
  success: boolean;
  message: string;
  fixture: RaceFixture[];
}

export interface RaceFixture {
  id: string;
  name: string;
  externalId: string;
  sport: string;
  status: string;
  startTime: string;
  inplay: boolean;
  isFancy: boolean;
  isBookmaker: boolean;
  isPremiumFancy: boolean;
  isPopular: boolean;
  competitionId: number;
  providerId: number | null;

  competition: {
    id: number;
    name: string;
    externalId: string;
    startDate: string | null;
  };

  markets: {
    id: string;
    name: string;
    externalId: string;
    startTime: string;
    runner: {
      handicap: number;
      runnerName: string;
      selectionId: string;
      sortPriority: string;
    }[];
  }[];
}
export interface TeamScore {
  name: string;
  score: string;
  runRate: number | null;
  requiredRate: number | null;
  isBatting: boolean;
}

export interface InningsInfo {
  currentOver: string;
  ballRunningStatus: string;
  day: string;
}

export interface MatchStatus {
  isFinished: boolean;
  message: string;
}
export interface ScorecardResponse {
  success: boolean;
  message: string;
  scorecard: Scorecard;
}

export interface Scorecard {
  matchId: string;
  matchName: string;
  betfairEventId: string;
  team1: Team;
  team2: Team;
  lastBalls: string[];
  innings: Innings;
  status: Status;
  updatedAt: number;
}

export interface Team {
  name: string;
  score: string;
  runRate: number | null;
  requiredRate: number | null;
  isBatting: boolean;
}

export interface Innings {
  currentOver: string;
  ballRunningStatus: string;
  day: string;
}

export interface Status {
  isFinished: boolean;
  message: string;
}
export interface FixtureCountsBucket {
  all: number;
  sports: Record<string, number>;
}

export interface FixtureCountsResponse {
  success: boolean;
  message: string;
  counts: {
    inplay: FixtureCountsBucket;
    today: FixtureCountsBucket;
    upcoming: FixtureCountsBucket;
  };
}

export interface LiveScorecardBatsman {
  player_id: number;
  name: string;
  img: string;
  strike_status: number;
  run: number;
  ball: number;
  fours: number;
  sixes: number;
  strike_rate: string;
}

export interface LiveScorecardBowler {
  player_id: number;
  name: string;
  img: string;
  run: number;
  maiden: number;
  over: string;
  wicket: number;
  economy: string;
}

export interface LiveScorecardInnings {
  score: string | number;
  wicket: string | number;
  ball: number;
  over: string;
  declare: number;
}

export interface LiveScorecardTeamScores {
  team_id: number;
  [inning: string]: LiveScorecardInnings | number;
}

export interface LiveScorecardOverSummary {
  over: number;
  balls: string[];
  runs: number;
}

export interface LiveScorecardData {
  match_id: number;
  series_id: number;
  match_type: string;
  toss: string;
  result: string;
  first_circle?: string;
  second_circle?: string;
  team_a_id: number;
  team_a: string;
  team_a_short: string;
  team_a_img: string;
  team_b_id: number;
  team_b: string;
  team_b_short: string;
  team_b_img: string;
  current_inning: string;
  batsman: LiveScorecardBatsman[];
  bolwer: LiveScorecardBowler;
  last_bolwer: LiveScorecardBowler;
  balling_team: string;
  batting_team: string;
  team_a_score: LiveScorecardTeamScores;
  team_a_scores: string;
  team_a_over: string;
  team_b_score: LiveScorecardTeamScores;
  team_b_scores: string;
  team_b_over: string;
  trail_lead: string;
  c_team_score: number;
  curr_rate: string;
  target: number;
  need_run_ball: string;
  rr_rate: string;
  last4overs: LiveScorecardOverSummary[];
  last36ball: string[];
}

export interface LiveScorecardResponse {
  success: boolean;
  message: string;
  scorecard: {
    status: boolean;
    msg: string;
    data: LiveScorecardData | [];
  };
}

export const eventsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getFixtureCounts: builder.query<FixtureCountsResponse, void>({
      query: () => ({
        url: '/fixture/counts',
        method: 'GET',
      }),
      providesTags: ['Events'],
    }),
    getEvents: builder.query<ApiResponse, EventsQueryParams>({
      query: ({ sportName, competitionId, search, inplay, matchTime, status } = {}) => {
        const params = new URLSearchParams();

        if (sportName) params.append('sport', sportName);
        if (competitionId) params.append('competitionId', String(competitionId));
        if (inplay) params.append('inplay', String(inplay));
        if (search) params.append('search', search);
        if (matchTime) params.append('matchTime', matchTime);
        if (status) params.append('status', status);

        const queryString = params.toString();
        return {
          url: `/fixture${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Events'],
    }),

    getScorecard: builder.query<any, number>({
      query: (eventId: number) => ({
        url: `events/scorecard/${eventId}`,
        method: 'GET',
      }),
    }),
    getRaces: builder.query<
      RaceFixtureResponse,
      {
        sportName?: string;
        inplay?: boolean;
        seriesId?: string;
        search?: string;
        status?: string;
      }
    >({
      query: ({ inplay, sportName, seriesId, search, status }) => ({
        url: '/fixture/race',
        params: {
          sport: sportName ?? undefined,
          competitionId: seriesId,
          search,
          inplay,
          status,
        },
      }),
      providesTags: ['Sports'],
    }),
    getRaceDetails: builder.query<RaceCatalogueResponse, { eventId: string }>({
      query: ({ eventId }) => {
        let url = `/catalogue/${eventId}?t=${Date.now()}`;
        return url;
      },
      providesTags: ['Sports'],
    }),
    getRawScorecard: builder.query<ScorecardResponse, number>({
      query: (eventId: number) => ({
        url: `/events/raw-scorecard/${eventId}`,
        method: 'GET',
      }),
    }),
    getLiveScorecard: builder.query<LiveScorecardResponse, number>({
      query: (eventId: number) => ({
        url: `/events/raw-scorecard/${eventId}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetFixtureCountsQuery,
  useGetEventsQuery,
  useGetRacesQuery,
  useGetScorecardQuery,
  useGetRaceDetailsQuery,
  useGetRawScorecardQuery,
  useGetLiveScorecardQuery,
} = eventsApi;
